import type { Coupon, TopCoupon } from '@agos/api-client/model'
import { tt } from '@agos/shared'
import type { BadgeColor } from '@agos/ui'

/**
 * A coupon's status is derived, never stored.
 *
 * The precedence matters and is ported exactly: a coupon that is switched off
 * *and* expired reads as **inactive**, not expired, and the status filter runs
 * on this same derivation — so the two can never disagree.
 *
 * `docs/DB-AUDIT.md` finding 9 lived here. Coupon OBED2026 expires in the year
 * 0026; legacy could not parse the date and treated the coupon as never
 * expiring, while a correct reading calls it expired. The backend has since
 * fixed the row, but the derivation is what made the difference visible.
 */
export type CouponStatus = 'inactive' | 'expired' | 'used_up' | 'active'

export interface CouponStatusInfo {
  status: CouponStatus
  label: string
  color: BadgeColor
}

export function couponStatus(coupon: Coupon, now: Date): CouponStatusInfo {
  if (!coupon.active) {
    // FB-027: legacy reads "Neaktivny" — Slovak with the diacritics stripped.
    return { status: 'inactive', label: tt('Neaktívny'), color: 'zinc' }
  }
  if (coupon.expiresAt && new Date(coupon.expiresAt) < now) {
    return { status: 'expired', label: tt('Expirovaný'), color: 'red' }
  }
  if (coupon.maxUses != null && (coupon.usedCount ?? 0) >= coupon.maxUses) {
    return { status: 'used_up', label: tt('Vyčerpaný'), color: 'amber' }
  }
  return { status: 'active', label: tt('Aktívny'), color: 'green' }
}

/** `percent` and `fixed` are the only two the UI can render. */
export type DiscountType = 'percent' | 'fixed'

/**
 * The API types `type` as a bare string, so a third value could arrive. Falling
 * back to `fixed` is the safe reading: a percentage misread as a fixed amount
 * discounts less, never more.
 */
export function discountType(value: string | undefined): DiscountType {
  return value === 'percent' ? 'percent' : 'fixed'
}

/**
 * A coupon in *Najpoužívanejšie kupóny*, carrying the place it holds — §9.284.
 *
 * The rank travels **on the row** because `DataTable` gives `render` the index within the sorted and
 * paged rows. A badge built from that would renumber 1…n on every sort, so sorting by code would
 * assert that alphabetical order is usage order, and page two would begin again at 1.
 */
export interface RankedCoupon extends TopCoupon {
  rank: number
}

/**
 * The server's ordering, made explicit.
 *
 * `GET /discounts/stats` returns `topCoupons` already sorted by usage, so the rank is the position
 * in the array. It is not recomputed from `usageCount` here: a second opinion could disagree with
 * the endpoint about a tie and put a 2 above a 1 on a list headed *most used*.
 */
export function rankTopCoupons(coupons: TopCoupon[] | undefined): RankedCoupon[] {
  return (coupons ?? []).map((coupon, index) => ({ ...coupon, rank: index + 1 }))
}

/** The alphabet has no I, O, 0 or 1 — customers read these out over the phone. */
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
const CODE_LENGTH = 8

export function generateCouponCode(): string {
  const randomBytes = new Uint32Array(CODE_LENGTH)
  crypto.getRandomValues(randomBytes)
  return Array.from(
    randomBytes,
    (value) => CODE_ALPHABET[value % CODE_ALPHABET.length] ?? '',
  ).join('')
}

/**
 * Where a coupon came from, in words - §9.294, `BACKEND.md` §10A.98.
 *
 * The owner: *"add from WHERE is that cupon come from. If its manually created (by whom), or winned
 * by game (which one)… also like if its from Campaing and from which campain"*.
 *
 * **Null is not "manual" and must not read as it.** 3 518 coupons predate the column and no origin
 * for them is recoverable anywhere, so they say *neznámy pôvod* - which is the truth rather than a
 * guess that would make every one of them look hand-typed.
 */
export function couponOrigin(coupon: { originKind?: string | null; originDetail?: string | null }): string {
  const detail = (coupon.originDetail ?? '').trim()
  switch (coupon.originKind) {
    case 'manual':
      return detail === '' ? tt('Ručne vytvorený') : `${tt('Ručne')}: ${detail}`
    case 'campaign':
      return detail === '' ? tt('Kampaň') : `${tt('Kampaň')}: ${detail}`
    case 'mini_game':
      return detail === '' ? tt('Výhra v hre') : `${tt('Výhra v hre')}: ${detail}`
    case 'wheel':
      return tt('Koleso šťastia')
    case 'organization':
      // §9.782 - an organization's code, managed on the campaigns tab; its detail is the organization's id,
      // which says nothing to a person, so the label stops at the kind.
      return tt('Cashback pre organizáciu')
    default:
      // Minted before the origin was recorded. Saying so beats implying somebody typed it.
      return tt('Neznámy pôvod')
  }
}

/**
 * *Rozdelenie zliav* - where the discount given away came from, as a share - §9.297.
 *
 * Legacy's own panel (`coupons.tsx`, *"Rozdelenie zliav"*), which the port left out. The bars are
 * drawn from totals the summary already answered, so this needed no new data - it is the cheapest of
 * the owner's *"add more Štatistiky"* and the only one that is pure parity.
 *
 * **Three shares since §10A.173, and the third is what makes the bar honest.** It was two, and the
 * coupon half was *everything not labelled manual* - 845 of its 1 589 orders had no coupon at all.
 * The remainder is its own share now, so the bars still account for the whole and neither of the two
 * named ones claims money it cannot explain. `other` is optional so a caller reading an older
 * response behaves as it did.
 *
 * **Nought over nought is nought, not `NaN`.** A period with no discounts at all is an ordinary state
 * for a day filter, and a bar of width `NaN%` renders as a full bar in every browser.
 */
export function discountShare(
  couponTotal: number,
  manualTotal: number,
  otherTotal = 0,
): {
  couponPercent: number
  manualPercent: number
  otherPercent: number
} {
  const total = couponTotal + manualTotal + otherTotal
  if (!(total > 0)) return { couponPercent: 0, manualPercent: 0, otherPercent: 0 }
  return {
    couponPercent: (couponTotal / total) * 100,
    manualPercent: (manualTotal / total) * 100,
    otherPercent: (otherTotal / total) * 100,
  }
}

/** The origins §10A.101 counts, in the order the panel lists them. */
export function originLabel(origin: string): string {
  if (origin === 'campaign') return tt('Kampane')
  if (origin === 'mini_game') return tt('Hry')
  if (origin === 'wheel') return tt('Koleso šťastia')
  if (origin === 'manual') return tt('Ručne vytvorené')
  if (origin === 'organization') return tt('Organizácie')
  return tt('Pred zavedením evidencie')
}
